import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TeamMember } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>);

const SortableTeamMemberItem: React.FC<{ member: TeamMember, onDelete: (member: TeamMember) => void }> = ({ member, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: member.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <tr ref={setNodeRef} style={style}>
            <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm w-12">
                <button {...attributes} {...listeners} className="cursor-grab p-2 text-gray-400 hover:text-gray-700">
                    <MenuIcon className="h-5 w-5" />
                </button>
            </td>
            <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                <img src={member.imageUrl} alt={member.name} className="w-12 h-12 rounded-full object-cover" />
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap font-semibold">{member.name}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap">{member.role}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap">{member.category}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                <Link to={`/admin/team/edit/${member.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</Link>
                <button onClick={() => onDelete(member)} className="text-red-600 hover:text-red-900">Delete</button>
            </td>
        </tr>
    );
};

const AdminTeamPage: React.FC = () => {
    const { data: teamMembers, setData: setTeamMembers, isLoading } = useCollection<TeamMember>('teamMembers', { field: 'order', direction: 'asc' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (member: TeamMember) => {
        setMemberToDelete(member);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!memberToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'teamMembers', memberToDelete.id));
            setTeamMembers(prev => prev.filter(m => m.id !== memberToDelete.id));
            showToast('Team member removed successfully.', { type: 'success' });
        } catch (error) {
            showToast("Error: Could not remove the team member.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setMemberToDelete(null);
            setIsDeleting(false);
        }
    };
    
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (teamMembers && over && active.id !== over.id) {
            const oldIndex = teamMembers.findIndex((p) => p.id === active.id);
            const newIndex = teamMembers.findIndex((p) => p.id === over.id);
            const newOrderMembers = arrayMove(teamMembers, oldIndex, newIndex);
            
            setTeamMembers(newOrderMembers);

            try {
                const batch = writeBatch(db);
                newOrderMembers.forEach((member, index) => {
                    const memberRef = doc(db, 'teamMembers', member.id);
                    batch.update(memberRef, { order: index });
                });
                await batch.commit();
                showToast('Team member order updated.', { type: 'success' });
            } catch (error) {
                showToast('Failed to update team member order.', { type: 'error' });
                setTeamMembers(teamMembers);
            }
        }
    };

    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Remove Team Member"
                description={`Are you sure you want to remove ${memberToDelete?.name} from the team? This action cannot be undone.`}
                isConfirming={isDeleting}
            />
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Team</h2>
                <Link to="/admin/team/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Member
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading team members...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 w-12"></th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Photo</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Designation</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={teamMembers?.map(m => m.id) || []} strategy={verticalListSortingStrategy}>
                                <tbody>
                                    {(teamMembers || []).map(member => (
                                        <SortableTeamMemberItem key={member.id} member={member} onDelete={openDeleteModal} />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                )}
                {!isLoading && teamMembers && teamMembers.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No team members found.</p>
                        <p className="mt-2"><Link to="/admin/team/new" className="text-indigo-600 hover:underline">Add your first team member!</Link></p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminTeamPage;